import { useState } from 'react';
import { Badge, Button, DialogShell, Panel, fieldClass, helpClass, labelClass } from '@timely/ui';
import {
    answerPromiseCall, presetDay, promiseState, suggestedNextFollowUp, type ActivityEntry,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import { DateField } from '../forms/DateField';
import { useFormatters } from './useFormatters';
import { answerPromise } from './useBook';

/**
 * The promises on this account nobody has answered yet, with the two answers
 * a collector gives (0033): kept, or not kept.
 *
 * The answer is a person's record, never an inference — the product does not
 * see the bank. Kept is written as a payment answering the promise; not kept
 * as a note answering it. Answering the account's latest promise closes it,
 * so it stops appearing under "Promise date passed", and a not-kept answer
 * puts the account under "Promise not kept" until something new is promised.
 */
export function OpenPromises({ promises, workspaceId, customerId, updatedAt, disabled, onAnswered, onError }: {
    promises: ActivityEntry[];
    workspaceId: string;
    customerId: string;
    updatedAt: string;
    disabled: boolean;
    onAnswered: (message: string) => Promise<void>;
    onError: (message: string) => void;
}) {
    const { money, date, today } = useFormatters();
    const [answering, setAnswering] = useState<{ promise: ActivityEntry; kept: boolean } | null>(null);

    if (promises.length === 0) return null;

    return (
        <Panel
            title={promises.length === 1 ? 'A promise to answer' : `${promises.length} promises to answer`}
            description="Did the money arrive as promised? Say so here, and the promise closes."
        >
            <ul className="divide-y divide-separator">
                {promises.map(promise => {
                    const state = promiseState(promise, undefined, today);
                    return (
                        <li key={promise.id} className="px-5 py-3 flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-body font-semibold text-label num">
                                        {promise.promisedAmount !== undefined ? money.exact(promise.promisedAmount) : 'An amount'}
                                    </span>
                                    <span className="text-secondary text-label-2">
                                        {promise.promisedOn ? `by ${date.long(promise.promisedOn)}` : 'no date given'}
                                    </span>
                                    {/* Unanswered past its date: waiting for an answer (amber). Crimson is for a promise answered as not kept. */}
                                    {state === 'overdue' && <Badge tone="warn">Date passed</Badge>}
                                    {state === 'due' && <Badge tone="warn">Due today</Badge>}
                                </div>
                                <div className="text-caption text-label-3 mt-0.5">
                                    Promised to {promise.authorName || 'somebody'} on {date.short(promise.createdAt)}
                                    {promise.body ? ` — “${promise.body}”` : ''}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-none">
                                <Button size="sm" variant="secondary" disabled={disabled}
                                    onClick={() => setAnswering({ promise, kept: true })}>
                                    Kept
                                </Button>
                                <Button size="sm" variant="secondary" disabled={disabled}
                                    onClick={() => setAnswering({ promise, kept: false })}>
                                    Not kept
                                </Button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {answering && (
                <AnswerDialog
                    promise={answering.promise}
                    kept={answering.kept}
                    onClose={() => setAnswering(null)}
                    onConfirm={async ({ note, nextFollowUp }) => {
                        await answerPromise(answerPromiseCall(workspaceId, customerId, answering.promise.id, answering.kept, {
                            note, nextFollowUp, expectedUpdatedAt: updatedAt,
                        }));
                        setAnswering(null);
                        await onAnswered(answering.kept ? 'Marked kept.' : 'Marked not kept.');
                    }}
                    onError={onError}
                />
            )}
        </Panel>
    );
}

function AnswerDialog({ promise, kept, onClose, onConfirm, onError }: {
    promise: ActivityEntry;
    kept: boolean;
    onClose: () => void;
    onConfirm: (answer: { note: string; nextFollowUp: string }) => Promise<void>;
    onError: (message: string) => void;
}) {
    const { money, date, today } = useFormatters();
    const [note, setNote] = useState('');
    // Not kept: ring again tomorrow, as after a missed promise. Kept: nothing is suggested.
    const [nextFollowUp, setNextFollowUp] = useState(kept ? '' : suggestedNextFollowUp({ kind: 'no_answer', promisedOn: '' }, today));
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const amount = promise.promisedAmount !== undefined ? money.exact(promise.promisedAmount) : 'the amount';
    const confirm = async () => {
        if (saving) return;
        setSaving(true);
        setProblem(null);
        try {
            await onConfirm({ note, nextFollowUp });
        } catch (failed) {
            const message = failed instanceof Error ? failed.message : 'That did not save.';
            setProblem(message);
            onError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogShell
            title={kept ? 'Mark this promise kept?' : 'Mark this promise not kept?'}
            subtitle={`${amount}${promise.promisedOn ? ` by ${date.long(promise.promisedOn)}` : ''}`}
            size="sm"
            onClose={onClose}
            closeOnEscape={!saving}
            onSubmit={e => { e.preventDefault(); void confirm(); }}
            footer={
                <>
                    <Button type="button" variant="quiet" disabled={saving} onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="primary" busy={saving}>
                        {saving ? 'Saving…' : kept ? 'Mark kept' : 'Mark not kept'}
                    </Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}
            <p className="text-body text-label-2 leading-relaxed mb-4">
                {kept
                    ? 'This records that the customer paid as promised. It is your record — the balance still comes from your books.'
                    : 'This records that the money did not arrive as promised. The account moves to “Promise not kept” until something new is promised.'}
            </p>
            <label htmlFor="answer-note" className={labelClass}>Anything to add (optional)</label>
            <textarea
                id="answer-note"
                rows={2}
                value={note}
                maxLength={3900}
                onChange={e => setNote(e.target.value)}
                className={fieldClass({ multiline: true, className: 'w-full resize-y' })}
            />
            <p className={helpClass}>Saved in the history with your answer.</p>
            <div className="mt-4">
                <DateField
                    id="answer-next" label="Next follow-up (optional)" presets={[1, 3, 7]} notBefore={presetDay(0, today)}
                    value={nextFollowUp} clearable onChange={setNextFollowUp}
                />
            </div>
        </DialogShell>
    );
}
